import type { DetectionParams } from '@audigoal/core';
import { toDb } from './dsp';
import { hzToMidi } from '../engine/timing';
import type { AnalysisFrame } from './frame-analyzer';

/**
 * Turns a stream of analysis frames into musical events (plan doc 11).
 *
 * The hard part is not finding a pitch — the frame analyzer does that — but
 * deciding when a note has *started*, which needs the pitch to settle, and when
 * it has *stopped*, which needs silence to persist. Both are debounced in
 * frames rather than milliseconds so an instrument's own hop size sets the
 * timing.
 */

export type NoteEvent =
  | { type: 'noteOn'; midi: number; cents: number; t: number }
  | { type: 'noteOff'; midi: number; t: number }
  | { type: 'pitch'; midi: number; cents: number; rms: number; t: number }
  | { type: 'stable'; midi: number; cents: number; durationMs: number; t: number }
  | { type: 'level'; db: number; t: number }
  | { type: 'silence'; t: number }
  | { type: 'clipping'; t: number };

export interface NoteTrackerOptions {
  detection: DetectionParams;
  sampleRate: number;
  a4Hz?: number;
  /** Written pitch to sounding pitch, for a transposing instrument. */
  transposeSemis?: number;
  /** Seconds of quiet before a `silence` event. */
  silenceAfterMs?: number;
  /**
   * A gate measured for this room by calibration.
   *
   * The adaptive floor still runs — a room changes while you practice — but it
   * starts from a measurement rather than from the minimum, so the very first
   * note of a session is judged against this room instead of an ideal one.
   */
  measuredGate?: number;
}

const MEDIAN_WINDOW = 5;
const NOISE_WINDOW_MS = 2000;
const GATE_MULTIPLIER = 3;
const MIN_GATE = 0.004;
const MAX_GATE = 0.05;
/** A pitch this far from the current note counts as a different note. */
const RETRIGGER_SEMITONES = 0.8;
const RETRIGGER_FRAMES = 3;
const STABLE_MS = 120;

export class NoteTracker {
  private readonly detection: DetectionParams;
  private readonly a4Hz: number;
  private readonly transposeSemis: number;
  private readonly silenceAfterMs: number;
  private readonly hopMs: number;

  private readonly measuredGate: number | null;
  private state: 'silent' | 'sounding' = 'silent';
  private noiseFloor = MIN_GATE;
  private median: number[] = [];

  private stableFrames = 0;
  private silentFrames = 0;
  private driftFrames = 0;

  private currentMidi: number | null = null;
  private currentMidiF = 0;
  private noteStartedAt = 0;
  private lastStableEmit = -Infinity;
  private lastSoundAt = 0;
  private silenceEmitted = false;
  private lastRms = 0;
  /** The last stable note, for the octave guard after an onset. */
  private previousStable: { midi: number; t: number } | null = null;

  constructor(options: NoteTrackerOptions) {
    this.detection = options.detection;
    this.a4Hz = options.a4Hz ?? 440;
    this.transposeSemis = options.transposeSemis ?? 0;
    this.silenceAfterMs = options.silenceAfterMs ?? 5000;
    this.hopMs = (options.detection.hopSize / options.sampleRate) * 1000;
    this.measuredGate = options.measuredGate ?? null;
    if (this.measuredGate !== null) this.noiseFloor = this.measuredGate / GATE_MULTIPLIER;
  }

  /** The level a frame must clear to be treated as sound rather than room. */
  get gate(): number {
    const adaptive = this.noiseFloor * GATE_MULTIPLIER;
    // Never drop below what calibration measured: the adaptive floor falls
    // toward the quietest recent frame, and a long quiet passage would
    // otherwise walk the gate down under the room's real level.
    const floor = this.measuredGate ?? MIN_GATE;
    return Math.min(MAX_GATE, Math.max(MIN_GATE, Math.max(floor, adaptive)));
  }

  reset(): void {
    this.state = 'silent';
    this.noiseFloor = (this.measuredGate ?? MIN_GATE) / GATE_MULTIPLIER;
    this.median = [];
    this.stableFrames = 0;
    this.silentFrames = 0;
    this.driftFrames = 0;
    this.currentMidi = null;
    this.previousStable = null;
    this.silenceEmitted = false;
  }

  /** Feeds one frame and returns whatever it implies. */
  push(frame: AnalysisFrame): NoteEvent[] {
    const events: NoteEvent[] = [];
    const tMs = frame.t * 1000;

    events.push({ type: 'level', db: toDb(frame.rms), t: frame.t });
    if (frame.clipping) events.push({ type: 'clipping', t: frame.t });

    this.trackNoiseFloor(frame.rms);

    if (frame.rms < this.gate) {
      this.silentFrames += 1;
      this.stableFrames = 0;
      this.median = [];
      if (this.state === 'sounding' && this.silentFrames >= this.detection.noteOffFrames) {
        const midi = this.currentMidi;
        this.state = 'silent';
        this.currentMidi = null;
        if (midi !== null) events.push({ type: 'noteOff', midi, t: frame.t });
      }
      if (
        this.state === 'silent' &&
        !this.silenceEmitted &&
        tMs - this.lastSoundAt >= this.silenceAfterMs
      ) {
        this.silenceEmitted = true;
        events.push({ type: 'silence', t: frame.t });
      }
      this.lastRms = frame.rms;
      return events;
    }

    this.lastSoundAt = tMs;
    this.silenceEmitted = false;
    this.silentFrames = 0;

    // Loud enough, but the pitch is not trustworthy: hold the current note
    // rather than dropping it, because a pick attack or a strum's noise burst
    // is unpitched for a few frames in the middle of a perfectly good note.
    if (frame.f0 <= 0 || frame.clarity < this.detection.clarityThreshold) {
      this.lastRms = frame.rms;
      return events;
    }

    const midiF = hzToMidi(frame.f0, this.a4Hz) - this.transposeSemis;
    if (!Number.isFinite(midiF)) {
      this.lastRms = frame.rms;
      return events;
    }

    const smoothed = this.pushMedian(midiF);
    const nearest = Math.round(smoothed);
    const cents = (smoothed - nearest) * 100;

    if (this.state !== 'sounding') {
      this.stableFrames += 1;
      if (this.stableFrames >= this.detection.noteOnFrames) {
        const midi = this.guardOctave(nearest, tMs);
        this.state = 'sounding';
        this.currentMidi = midi;
        this.currentMidiF = smoothed;
        this.noteStartedAt = tMs;
        this.lastStableEmit = -Infinity;
        events.push({ type: 'noteOn', midi, cents, t: frame.t });
      }
      this.lastRms = frame.rms;
      return events;
    }

    const drifted = Math.abs(smoothed - this.currentMidiF) > RETRIGGER_SEMITONES;
    const rose = toDb(frame.rms) - toDb(this.lastRms) >= 6;
    this.driftFrames = drifted ? this.driftFrames + 1 : 0;

    if (this.driftFrames >= RETRIGGER_FRAMES || (drifted && rose)) {
      const previous = this.currentMidi;
      if (previous !== null) events.push({ type: 'noteOff', midi: previous, t: frame.t });
      this.currentMidi = nearest;
      this.currentMidiF = smoothed;
      this.noteStartedAt = tMs;
      this.driftFrames = 0;
      this.lastStableEmit = -Infinity;
      events.push({ type: 'noteOn', midi: nearest, cents, t: frame.t });
      this.lastRms = frame.rms;
      return events;
    }

    // Only follow the pitch while it is *not* drifting. Updating it every frame
    // would make the reference chase the signal, so a jump to a new note would
    // read as no drift at all by the very next frame and never re-trigger.
    if (!drifted) this.currentMidiF = smoothed;
    events.push({ type: 'pitch', midi: nearest, cents, rms: frame.rms, t: frame.t });

    const heldMs = tMs - this.noteStartedAt;
    if (
      nearest === this.currentMidi &&
      heldMs >= STABLE_MS &&
      tMs - this.lastStableEmit >= this.hopMs
    ) {
      this.lastStableEmit = tMs;
      this.previousStable = { midi: nearest, t: tMs };
      events.push({ type: 'stable', midi: nearest, cents, durationMs: heldMs, t: frame.t });
    }

    this.lastRms = frame.rms;
    return events;
  }

  /**
   * What the room sounds like when nothing is being played.
   *
   * A literal running minimum over the last two seconds does not work: it
   * includes the current frame, so while a plucked note decays the minimum
   * follows it down, the gate sits at three times the present level, and the
   * note silences itself a few frames after it starts. That is a real failure,
   * not a theoretical one — it cut a ukulele note off after 64 ms.
   *
   * Instead the floor falls quickly toward a quiet frame and rises very slowly,
   * so a held note barely moves it while a genuinely noisier room is learned
   * over a second or two.
   */
  private trackNoiseFloor(level: number): void {
    const fall = 0.25;
    const rise = Math.min(0.02, this.hopMs / NOISE_WINDOW_MS);
    const alpha = level < this.noiseFloor ? fall : rise;
    this.noiseFloor = this.noiseFloor + (level - this.noiseFloor) * alpha;
  }

  private pushMedian(value: number): number {
    this.median.push(value);
    if (this.median.length > MEDIAN_WINDOW) this.median.shift();
    const sorted = [...this.median].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)] ?? value;
  }

  /**
   * MPM occasionally picks the sub-harmonic on a plucked attack. A candidate
   * exactly an octave below the note that was just sounding, within 40 ms of it,
   * is that slip rather than a new note.
   */
  private guardOctave(candidate: number, tMs: number): number {
    const previous = this.previousStable;
    if (previous === null) return candidate;
    if (tMs - previous.t > 40) return candidate;
    return previous.midi - candidate === 12 ? previous.midi : candidate;
  }
}
